import { useCallback, useEffect, useState } from "react";

export function useLocalStorage<T>(
    key: string,
    defaultValue: T,
    initialValue?: T,
): { value: T; update: (value: T) => void; remove: () => void } {
    const [value, setValue] = useState<T>(initialValue !== undefined ? initialValue : defaultValue);

    const update = useCallback((value: T) => {
        setValue(value);
        window.localStorage.setItem(key, JSON.stringify(value));
    }, []);

    const remove = useCallback(() => {
        setValue(value);
        window.localStorage.removeItem(key);
    }, []);

    useEffect(() => {
        const jsonValue = window?.localStorage.getItem(key);
        if (jsonValue) {
            try {
                const pValue = JSON.parse(jsonValue) as T;
                if (pValue !== value) {
                    setValue(pValue);
                }
                return;
            } catch (e) {
                console.error(e);
            }
        }

        setValue(defaultValue);
    }, []);

    useEffect(() => {
        const onLocalStorageChange = (e: StorageEvent) => {
            if (e.key === key) {
                const newValue = JSON.parse(e.newValue || "null") as T;
                if (newValue !== value) {
                    setValue(newValue);
                }
            }
        };

        window.addEventListener("storage", onLocalStorageChange, {
            passive: true,
        });
        return () => {
            window.removeEventListener("storage", onLocalStorageChange);
        };
    }, [value]);

    return { value, update, remove };
}
